import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import { ResourcesGrid, ResourceForm } from '../components/ResourceLibrary';
import useAuth from '../auth/useAuth';
import usePermissions from '../auth/usePermissions';
import usePlantScope from '../auth/usePlantScope';
import useIsMobile from '../auth/useIsMobile';
import {
  deleteSupplier,
  formatCoverage,
  getSupplierByCode,
  listSuppliers,
  assignmentHasEnded,
  poCarriesBalance,
  serviceLabel,
  updateSupplier,
} from '../services/suppliers';
import { listPartsForSupplier } from '../services/parts';
import { listPlants } from '../services/plants';
import {
  createSupplierDocument,
  deleteSupplierDocument,
  listSupplierDocuments,
} from '../services/supplierDocuments';
import { listInvoices } from '../services/invoices';
import {
  listSupplierActivityEvents,
  logContactAddedEvent,
  logSupplierPlantAssignmentEvent,
} from '../services/salesTracker';
import SupplierForm from './SupplierForm';
import {
  formatAddressLine,
  formatCurrency,
  formatDate,
  formatPhone,
} from '../utils/formatters';

// Shared width for the Plant/s column across Supplier Contacts, Plant
// Locations & Coverage, and Supplier Parts. Pinning this one column to
// the same width on all three tables keeps the left edge of the page
// visually aligned, even though the tables have different column counts
// overall. Wide enough to fit a SHAP + DODGE RAM pill pair on one line.
const PLANT_COL_WIDTH = 180;

/**
 * Supplier detail page — read-friendly view with the contact roster (QM,
 * QE, Facility Manager, etc.) broken out and an Edit button that reopens
 * the same form used on the Suppliers index. Plant Locations (the plants
 * this supplier bills into) are captured directly on the supplier.
 */
export default function SupplierDetail() {
  const { supplierCode } = useParams();
  const { name, email } = useAuth();
  // canEditSuppliers already accounts for mobile (edit/delete are off on
  // phones) and for any per-user Sales/Supplier override. canUpload gates the
  // Documents upload/remove controls — Director-only (off on mobile), matching
  // the portal's global file-upload rule.
  const { canEditSuppliers, canUpload, isDirector, isLiaisonManager } =
    usePermissions();
  // Plant-scoped users (Regional / Liaison Managers) can only click through to
  // plants assigned to them — others render as plain, non-clickable chips so
  // they never hit the "Plant Not Available" guard.
  const {
    scoped: plantScoped,
    allows: allowsPlant,
    allowsSupplier,
    loading: scopeLoading,
  } = usePlantScope();
  const isMobile = useIsMobile();
  const canEditHere = canEditSuppliers;
  const navigate = useNavigate();

  const [supplier, setSupplier] = useState(null);
  const [parts, setParts] = useState([]);
  const [plants, setPlants] = useState([]);
  // Other suppliers' codes — fed into SupplierForm so the Director-only
  // Account Number editor can flag duplicates without colliding with
  // this supplier's own current code. Fetched once on mount; refetched
  // after a save in case codes shifted.
  const [existingCodes, setExistingCodes] = useState([]);
  // Plant Locations & Coverage activity log — rows from SalesTrackerEvents
  // where Category = 'Plant Assignment' and SupplierCode matches. Fetched
  // client-side and reloaded after every save so a just-made edit shows
  // up in the Activity subsection without a manual refresh.
  const [activity, setActivity] = useState([]);
  // Coverage Activity is an audit trail of plant-assignment changes across
  // every plant this supplier serves. Plant-scoped users (Liaison /
  // Regional Managers) should only see the rows for the plants they're
  // assigned to — not activity at plants they don't manage. Supplier-level
  // events with no plant code are kept. Unscoped roles (Director /
  // Executive) see everything (per Jillian, Aug 2026).
  const visibleActivity = useMemo(() => {
    if (!plantScoped) return activity;
    return (activity || []).filter((ev) => {
      const pc = String(ev.PlantCode || '').trim();
      return !pc || allowsPlant(pc);
    });
  }, [activity, plantScoped, allowsPlant]);
  // Invoices billed under this supplier — used to draw down a Blanket PO
  // balance. Only fetched when the supplier has a code; filtered to Paid
  // on/after the PO initiation date in the plantPos memo below.
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  // Supplier Documents (contracts, COIs, W-9s, etc.) — stored in the
  // SupplierDocuments library, tagged by SupplierCode. Anyone viewing the
  // supplier can download; Directors upload/remove (canUpload).
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docUploadError, setDocUploadError] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getSupplierByCode(supplierCode);
      setSupplier(s);
      if (s?.SupplierCode) {
        try {
          const rows = await listPartsForSupplier(s.SupplierCode);
          setParts(rows);
        } catch {
          setParts([]);
        }
        try {
          const events = await listSupplierActivityEvents(s.SupplierCode);
          setActivity(events);
        } catch {
          setActivity([]);
        }
        // Invoices for this supplier — feeds the Blanket PO balance. The
        // SupplierCode is denormalised onto each invoice record, so we can
        // filter server-side. Best-effort: an empty list just shows the
        // full PO amount as remaining.
        try {
          const inv = await listInvoices({
            filter: `fields/SupplierCode eq '${s.SupplierCode}'`,
            top: 500,
          });
          setInvoices(inv || []);
        } catch {
          setInvoices([]);
        }
      } else {
        setParts([]);
        setActivity([]);
        setInvoices([]);
      }
      // Plants list powers the Plant Locations table (plant name + shift
      // count lookup) and the "Add part" modal's plant picker.
      try {
        const plantRows = await listPlants();
        setPlants(plantRows || []);
      } catch {
        setPlants([]);
      }
      // Suppliers list — only the codes are used here, fed into
      // SupplierForm as `existingCodes` so the Director-editable
      // Account Number field can flag duplicates against every other
      // supplier without false-positive-ing on this supplier's own
      // current code.
      try {
        const allSuppliers = await listSuppliers();
        setExistingCodes(
          (allSuppliers || [])
            .map((row) => row.SupplierCode)
            .filter(Boolean),
        );
      } catch {
        setExistingCodes([]);
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [supplierCode]);

  useEffect(() => {
    load();
  }, [load]);

  // Documents load on their own (independent of the main supplier fetch) so
  // an upload/remove can refresh just this list without re-pulling parts,
  // invoices, etc. Keyed off the resolved SupplierCode.
  const loadDocuments = useCallback(async () => {
    const code = supplier?.SupplierCode;
    if (!code) {
      setDocuments([]);
      return;
    }
    setDocsLoading(true);
    try {
      setDocuments(await listSupplierDocuments(code));
    } catch {
      setDocuments([]);
    } finally {
      setDocsLoading(false);
    }
  }, [supplier?.SupplierCode]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const onUploadDocument = async ({ file, Title, Description, Category }) => {
    setUploadingDoc(true);
    setDocUploadError('');
    try {
      await createSupplierDocument({
        file,
        supplierCode: supplier.SupplierCode,
        Title,
        Description,
        Category,
        PostedBy: name || email || '',
      });
      setShowDocUpload(false);
      await loadDocuments();
    } catch (err) {
      setDocUploadError(
        err?.body?.error?.message || err?.message || 'Upload failed.',
      );
    } finally {
      setUploadingDoc(false);
    }
  };

  const onRemoveDocument = async (doc) => {
    if (
      !window.confirm(
        `Delete "${doc.Title || doc.fileName}"? This can't be undone.`,
      )
    ) {
      return;
    }
    try {
      await deleteSupplierDocument(doc.id);
      await loadDocuments();
    } catch (err) {
      window.alert(
        `Couldn't delete the document: ${
          err?.body?.error?.message || err?.message || 'Unknown error.'
        }`,
      );
    }
  };

  const onSave = async (model, poFiles) => {
    setSaving(true);
    try {
      // Snapshot the pre-save plantAssignments + contacts so we can diff
      // against the new model after the write. Done before updateSupplier
      // so even if the save mutates the reference, we still have the
      // original shape.
      const oldAssignments = Array.isArray(supplier.plantAssignments)
        ? supplier.plantAssignments.map((pa) => ({ ...pa }))
        : [];
      const oldContacts = Array.isArray(supplier.contacts)
        ? supplier.contacts.map((c) => ({ ...c }))
        : [];
      await updateSupplier(supplier.id, model);
      setEditing(false);
      // Upload the attached PO files (one per plant PO, if any) to this
      // supplier's Documents so they're downloadable on the page.
      // Best-effort — the supplier itself already saved, so a failed
      // upload only warns.
      if ((poFiles || []).length > 0) {
        let uploadedAny = false;
        for (const { file, category, number, plantCode } of poFiles) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await createSupplierDocument({
              file,
              supplierCode: String(model.SupplierCode || supplier.SupplierCode || '').trim(),
              Title: `${category} ${number || ''}${plantCode ? ` (${plantCode})` : ''}`.trim(),
              Category: category,
              PostedBy: name || email || '',
            });
            uploadedAny = true;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[supplier] PO file upload failed', err);
            window.alert(
              `The supplier was saved, but the PO file upload for ${plantCode || 'a plant'} failed: ${
                err?.body?.error?.message || err?.message || 'Unknown error.'
              }`,
            );
          }
        }
        if (uploadedAny) await loadDocuments();
      }
      // Log each plant-assignment change to the Sales Tracker events list.
      // Writes are fire-and-forget — a failed event row shouldn't block
      // the reload or surface as a user-facing error (the actual save
      // already succeeded).
      try {
        const changes = diffPlantAssignments(
          oldAssignments,
          model.plantAssignments || [],
        );
        await Promise.all(
          changes.map((c) =>
            logSupplierPlantAssignmentEvent({
              supplierCode: supplier.SupplierCode,
              supplierName: supplier.Title || '',
              plantCode: c.plantCode,
              eventType: c.type,
              summary: c.summary,
              previousHours: c.previousHours,
              newHours: c.newHours,
              previousStatus: c.previousStatus,
              newStatus: c.newStatus,
              hoursUnit: c.hoursUnit,
              postedBy: name || email || '',
            }).catch((err) => {
              // eslint-disable-next-line no-console
              console.warn('[supplier] activity log write failed', err);
            }),
          ),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[supplier] activity diff failed', err);
      }
      // Log each new contact row — additions only, per the Dashboard's
      // "Contact Changes" brief. Reuses the same fire-and-forget pattern.
      try {
        const newContacts = diffAddedContacts(oldContacts, model.contacts || []);
        await Promise.all(
          newContacts.map((c) =>
            logContactAddedEvent({
              parentType: 'Supplier',
              parentCode: supplier.SupplierCode,
              parentName: supplier.Title,
              contactName: c.name || '',
              contactTitle: c.title || '',
              postedBy: name || email || '',
            }).catch((err) => {
              // eslint-disable-next-line no-console
              console.warn('[supplier] contact log write failed', err);
            }),
          ),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[supplier] contact diff failed', err);
      }
      // If a Director changed the Account Number (SupplierCode) on
      // save, the URL still points at the old code — `load()` would
      // 404 on the next fetch. Detect the rename and bounce the
      // browser to the new code's URL. `replace: true` so the back
      // button doesn't trap the Director on the broken old URL.
      const newCode = String(model.SupplierCode || '').trim().toUpperCase();
      const oldCode = String(supplier.SupplierCode || '').trim().toUpperCase();
      if (newCode && newCode !== oldCode) {
        navigate(`/suppliers/${encodeURIComponent(newCode)}`, { replace: true });
        return;
      }
      await load();
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (
      !window.confirm(
        `Delete ${supplier.Title}? This can't be undone.`,
      )
    ) {
      return;
    }
    await deleteSupplier(supplier.id);
    navigate('/suppliers');
  };

  // ---- Per-plant POs -----------------------------------------------------
  // One entry per plant assignment that carries a PO (the service's decode
  // carries a legacy supplier-level PO onto the first plant, so
  // pre-migration suppliers still show one). Blanket POs get a running
  // balance: Paid invoices issued inside the PO's date window draw the
  // balance down by each invoice's Total. An invoice counts toward a
  // plant's PO when its plantCodes include that plant; invoices with no
  // recorded plants (legacy rows) count only when this is the supplier's
  // lone PO, which preserves the old single-PO behaviour.
  const plantPos = useMemo(() => {
    if (!supplier) return [];
    // One PO per PLANT: a plant with several service rows (Sep 2026)
    // shares one PO across them, so only the first row per plant counts.
    const seenPoPlants = new Set();
    const pos = (supplier.plantAssignments || [])
      .filter((pa) => pa?.po?.type)
      .filter((pa) => {
        const code = String(pa.plantCode || '').toUpperCase();
        if (seenPoPlants.has(code)) return false;
        seenPoPlants.add(code);
        return true;
      })
      .map((pa) => ({
        plantCode: String(pa.plantCode || '').toUpperCase(),
        po: pa.po,
      }));
    // A PO shared across plants (the form's "All Plants" option saves
    // the same PO onto every assignment) shows as ONE card with the
    // plants pooled — otherwise the same dollar amount would be counted
    // once per plant. POs group when they share a type plus a non-empty
    // number, amount, and date window; unnumbered POs never group.
    const groups = new Map();
    for (const { plantCode, po } of pos) {
      const num = String(po.number || '').trim().toUpperCase();
      const key = num
        ? [
            po.type,
            num,
            Number(po.amount) || 0,
            toIsoDate(po.initiationDate),
            toIsoDate(po.expirationDate),
          ].join('|')
        : `__solo__:${plantCode}`;
      if (!groups.has(key)) groups.set(key, { po, plantCodes: [] });
      groups.get(key).plantCodes.push(plantCode);
    }
    const totalAssignments = new Set(
      (supplier.plantAssignments || []).map((pa) =>
        String(pa.plantCode || '').toUpperCase(),
      ),
    ).size;
    const grouped = Array.from(groups.values()).map(({ po, plantCodes }) => ({
      po,
      plantCodes,
      plantLabel:
        plantCodes.length > 1 && plantCodes.length === totalAssignments
          ? 'All Plants'
          : plantCodes.join(', '),
    }));
    // "Single" counts BALANCE-carrying POs only (Yearly/Quarterly,
    // matching the PO Tracker) — legacy invoices with no recorded plants
    // should still draw down a supplier's lone balance even if another
    // plant holds a Monthly PO, since Monthly / PO Pending don't track a
    // balance at all.
    const single =
      grouped.filter((g) => poCarriesBalance(g.po.type)).length === 1;
    return grouped.map(({ plantCodes, plantLabel, po }) => {
      const plantSet = new Set(plantCodes);
      // isBlanket = "carries a running balance" (Yearly / Quarterly).
      const isBlanket = poCarriesBalance(po.type);
      const amount = Number(po.amount) || 0;
      if (!isBlanket) {
        // Monthly / PO Pending have no running balance — just the fields.
        return { plantCodes, plantLabel, po, amount, isBlanket, rows: [], invoiced: 0, remaining: null };
      }
      const initiationIso = toIsoDate(po.initiationDate);
      // Optional expiration — bounds the ISSUE date window. An invoice
      // issued in-window still draws the balance down even if it isn't
      // marked Paid until after the PO expires.
      const expirationIso = toIsoDate(po.expirationDate);
      const paid = (invoices || [])
        .filter((inv) => (inv.Status || '') === 'Paid')
        .filter((inv) => {
          const codes = (inv.plantCodes || []).map((c) => String(c).toUpperCase());
          if (codes.length === 0) return single;
          return codes.some((c) => plantSet.has(c));
        })
        .filter((inv) => {
          const d = toIsoDate(inv.IssueDate);
          if (initiationIso && (!d || d < initiationIso)) return false;
          if (expirationIso && (!d || d > expirationIso)) return false;
          return true;
        })
        .sort((a, b) =>
          toIsoDate(a.IssueDate).localeCompare(toIsoDate(b.IssueDate)),
        );
      let running = amount;
      const rows = paid.map((inv) => {
        const total = Number(inv.Total) || 0;
        running -= total;
        return { invoice: inv, total, balanceAfter: running };
      });
      const invoiced = rows.reduce((acc, r) => acc + r.total, 0);
      return {
        plantCodes,
        plantLabel,
        po,
        amount,
        isBlanket,
        rows,
        invoiced,
        remaining: amount - invoiced,
      };
    });
  }, [supplier, invoices]);

  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load This Supplier</div>
        <p>{error.message}</p>
        <button className="btn btn-outline btn-sm" onClick={load}>
          Retry
        </button>
      </div>
    );
  }
  if (!supplier) {
    return (
      <div className="card">
        <div className="card-title">Supplier Not Found</div>
        <p>
          No supplier with code <strong>{supplierCode}</strong>.
        </p>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/suppliers')}>
          Back to Suppliers
        </button>
      </div>
    );
  }
  // Liaison Managers only see suppliers with coverage at their assigned
  // plants (per Jillian, July 2026) — this guard covers direct links the
  // index's own filtering can't catch. Parts-derived plants count too,
  // matching how the index decides which plants a supplier touches.
  if (isLiaisonManager) {
    if (scopeLoading) return <Loading />;
    const supplierPlantCodes = new Set(
      [
        ...(supplier.plantAssignments || []).map((pa) => pa.plantCode),
        ...(parts || []).flatMap((p) =>
          (p.plantVehicles || []).map((pv) => pv.plantCode),
        ),
      ]
        .filter(Boolean)
        .map((c) => String(c).toUpperCase()),
    );
    const anyAllowed =
      Array.from(supplierPlantCodes).some((c) => allowsPlant(c)) &&
      // Sep 2026: and the LM must be responsible for this supplier (a
      // plant with several LMs splits its suppliers between them).
      allowsSupplier(supplier.SupplierCode);
    if (!anyAllowed) {
      return (
        <div className="card">
          <div className="card-title">Supplier Not Available</div>
          <p className="muted">
            This supplier doesn&apos;t have coverage at any of your
            assigned plants.
          </p>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => navigate('/suppliers')}
          >
            Back to Suppliers
          </button>
        </div>
      );
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>
            <Link to="/suppliers">Suppliers</Link> / {supplier.SupplierCode}
          </div>
          <h1 style={{ margin: '4px 0 0' }}>{supplier.Title}</h1>
        </div>
        <div className="actions">
          <button className="btn btn-outline" onClick={() => navigate('/suppliers')}>
            ← Suppliers
          </button>
          {canEditHere && (
            <button className="btn btn-primary" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
      </div>

      {/* ---------- Supplier Info (identity + cadence) ----------
          All four identifying fields share a single row, but the
          first three are short labels (numbers / one-word cadence)
          while Address can be a long string — so we use an explicit
          1fr / 1fr / 1fr / 2fr column spec instead of the standard
          DetailGrid to give Address twice the horizontal budget.
          Billing-side cadence and terms live in the separate
          Billing card below. */}
      <div className="card">
        <div className="card-title">Supplier Info</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 2fr',
            gap: 12,
          }}
        >
          <Field label="DUNS Number" value={supplier.DunsNumber} />
          <Field label="Account Number" value={supplier.SupplierCode} />
          <Field label="Reporting Schedule" value={supplier.ReportingCadence} />
          {/* Address — single-line, soaks up the wider 2fr column.
              The form still captures Street / City / State / Postal
              / Country as separate inputs; this is just the read
              view. */}
          <Field
            label="Address"
            value={
              formatAddressLine({
                street: supplier.AddressStreet || supplier.Address,
                city: supplier.City,
                state: supplier.State,
                postalCode: supplier.PostalCode,
                country: supplier.Country,
              }) || ''
            }
          />
        </div>
        {supplier.NewSupplierPaperworkDate && (
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              color: 'var(--brand-muted)',
            }}
          >
            Approved Vendor Form completed on{' '}
            {formatPaperworkDate(supplier.NewSupplierPaperworkDate)}
          </div>
        )}
      </div>

      {/* ---------- Billing (cadence + terms) ----------
          Pulled out of Supplier Info so the AR-facing data lives in
          its own section the Director can scan independently — same
          three fields the Invoice draft flow reads when composing
          the customer-facing invoice. Director-only (per Jillian,
          July 2026): billing terms and POs are money matters, so
          everyone else's supplier page skips straight from Supplier
          Info to Plant Locations. */}
      {isDirector && (
      <div className="card">
        <div className="card-title">Billing</div>
        <DetailGrid>
          <Field
            label="Bill Payment Schedule"
            value={supplier.BillingCycle}
          />
          <Field label="PO Required" value={supplier.PoRequired} />
          {supplier.PoRequired === 'Yes' && (
            <Field
              label="PO Types"
              // Per-plant summary ("SHAP: Blanket PO, OAK: Monthly PO").
              // Falls back to the legacy supplier-level PoType for records
              // saved before per-plant POs that have no plant assignments
              // to carry the PO onto.
              value={
                plantPos.length
                  ? plantPos
                      .map((p) => `${p.plantLabel}: ${p.po.type}`)
                      .join(', ')
                  : supplier.PoType || '—'
              }
            />
          )}
          <Field label="Payment Terms" value={supplier.PaymentTerms} />
        </DetailGrid>
        {/* Overage-approval policy — shown as a checked line below the
            grid so a Director can see at a glance whether this supplier
            requires written approval when hours go over standard. Hidden
            when the rule isn't on, to keep the card uncluttered. */}
        {supplier.RequireOverageApproval === 'Yes' && (
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              color: 'var(--brand-muted)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <input type="checkbox" checked readOnly />
            <span>Requires written approval for overage (hours over standard)</span>
          </div>
        )}
      </div>
      )}

      {/* ---------- Plant POs ----------
          One card per plant PO (the PO section repeats per plant on the
          form). Monthly POs show the number and amount; Blanket POs
          additionally show the date window, how much has been invoiced
          (Paid invoices for that plant inside the window), and the
          remaining balance — followed by the itemised list of invoices
          drawing the balance down. PO files live in Documents below.
          Director-only, same as the Billing card above. */}
      {isDirector && plantPos.map(
        ({ plantCodes, plantLabel, po, amount, isBlanket, rows, invoiced, remaining }) => (
          <div className="card" key={`po-${plantCodes.join('-')}`}>
            <div className="card-title">
              {po.type} — {plantLabel}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 16,
                marginBottom: isBlanket ? 16 : 0,
              }}
            >
              <Field label="PO Number" value={po.number || '—'} />
              <Field
                label="PO Amount"
                value={po.amount != null ? formatCurrency(amount) : '—'}
              />
              {isBlanket && (
                <>
                  <Field
                    label="Start Date"
                    value={formatDate(po.initiationDate)}
                  />
                  <Field
                    label="Expiration Date"
                    value={
                      po.expirationDate ? formatDate(po.expirationDate) : '—'
                    }
                  />
                  <Field
                    label="Invoiced to Date"
                    value={formatCurrency(invoiced)}
                  />
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>
                      Remaining Balance
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color:
                          remaining < 0
                            ? 'var(--brand-danger)'
                            : 'var(--brand-ink)',
                      }}
                    >
                      {formatCurrency(remaining)}
                      {remaining < 0 && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            fontWeight: 500,
                            color: 'var(--brand-danger)',
                          }}
                        >
                          over PO
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            {po.notes && (
              <div style={{ marginTop: isBlanket ? 4 : 12 }}>
                <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>
                  Notes
                </div>
                <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
                  {po.notes}
                </div>
              </div>
            )}
            {isBlanket &&
              (rows.length ? (
                <table className="mini-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Issue Date</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th style={{ textAlign: 'right' }}>Balance After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ invoice, total, balanceAfter }) => (
                      <tr key={invoice.id}>
                        <td>{invoice.InvoiceNumber || '—'}</td>
                        <td>{formatDate(invoice.IssueDate)}</td>
                        <td style={{ textAlign: 'right' }}>
                          {formatCurrency(total)}
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            color:
                              balanceAfter < 0
                                ? 'var(--brand-danger)'
                                : 'inherit',
                          }}
                        >
                          {formatCurrency(balanceAfter)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="muted" style={{ margin: 0 }}>
                  No paid invoices have been billed against this PO yet —
                  the full amount remains.
                </p>
              ))}
          </div>
        ),
      )}

      {/* ---------- Supplier Contacts ---------- */}
      {/* Per-contact plant assignments were retired — contacts are
          now plain "who to call" entries at the supplier level.
          Plant Locations & Coverage and Supplier Parts still pin
          their leftmost Plant/s column to PLANT_COL_WIDTH so those
          two tables continue to visually align. */}
      <div className="card">
        <div className="card-title">Supplier Contacts</div>
        {supplier.contacts?.length ? (
          <table className="mini-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Title</th>
                <th>Location</th>
                <th>Email</th>
                <th>Cell Phone</th>
                {!isMobile && <th>Office Phone</th>}
              </tr>
            </thead>
            <tbody>
              {supplier.contacts.map((c, i) => {
                return (
                  <tr key={i}>
                    <td>{c.name || '—'}</td>
                    <td>{c.title || '—'}</td>
                    <td>{c.location || '—'}</td>
                    <td>{c.email || '—'}</td>
                    <td>{formatPhone(c.phone) || '—'}</td>
                    {!isMobile && <td>{formatPhone(c.officePhone) || '—'}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="muted">No contacts listed yet.</p>
        )}
      </div>

      {/* ---------- Plant Locations (direct per-plant rates/hours) ----
          This is the manually-entered roster of plants this supplier
          bills into, with the per-plant Hourly Rate and Coverage Hours
          captured on the form. Distinct from the auto-derived "OEM
          Plants" card below — that one infers membership from Parts.
          The card also renders whenever there's historical activity,
          even if every assignment has since been removed, so the audit
          trail doesn't disappear with the last row. */}
      {((supplier.plantAssignments && supplier.plantAssignments.length > 0) ||
        visibleActivity.length > 0) && (
        <div className="card">
          <div className="card-title">Plant Locations &amp; Coverage</div>
          {supplier.plantAssignments && supplier.plantAssignments.length > 0 ? (
          <table className="mini-table">
            <thead>
              <tr>
                <th style={{ width: PLANT_COL_WIDTH }}>Plant/s</th>
                {/* Hourly Rate is hidden from Liaison Managers (per
                    Jillian, Aug 2026) — they don't need to see the
                    supplier's billing rate. */}
                {!isLiaisonManager && <th>Hourly Rate</th>}
                {!isMobile && <th>Hours</th>}
                {!isMobile && <th>Unit</th>}
                <th>Total Hours/Day</th>
                {/* PO numbers are Director-only, matching the Billing
                    and Plant PO cards (per Jillian, July 2026). */}
                {!isMobile && isDirector && <th>Current PO</th>}
                <th>Coverage Status</th>
                {!isMobile && <th>Start Date</th>}
                {!isMobile && <th>End Date</th>}
              </tr>
            </thead>
            <tbody>
              {supplier.plantAssignments.map((pa, i) => {
                const plant = (plants || []).find(
                  (p) =>
                    String(p.PlantCode || '').toUpperCase() ===
                    String(pa.plantCode || '').toUpperCase(),
                );
                // Shifts/Day comes from the plant record (ShiftsPerDay,
                // a "1"-"6" dropdown on the Plant form). When the
                // assignment is billed "Per Shift," we use it both to
                // annotate the Hours Unit cell and to compute the
                // daily total below.
                const shiftsPerDay = plant?.ShiftsPerDay;
                const isPerShift =
                  (pa.hoursUnit || '').toLowerCase() === 'per shift';
                const hoursUnitLabel = isPerShift && shiftsPerDay
                  ? `Per Shift (${shiftsPerDay} shift${
                      String(shiftsPerDay) === '1' ? '' : 's'
                    })`
                  : pa.hoursUnit || '—';
                const totalPerDay = computeHoursPerDay(
                  pa.hoursAllocated,
                  pa.hoursUnit,
                  shiftsPerDay,
                );
                // Per-plant notes are shown only in the edit form now, not
                // on the read-only page (per Jillian, Oct 2026).
                const noteText = '';
                return (
                  <Fragment key={`${pa.plantCode}-${i}`}>
                    <tr>
                      {/* Render the plant as the purple "plant" pill
                          showing the plant code (e.g. SHAP) —
                          consistent with how plants appear everywhere
                          else in the portal (Supplier Parts,
                          Contacts, Parts index). The pill is wrapped
                          in a Link so clicking it still jumps to the
                          plant detail page. */}
                      <td>
                        {pa.plantCode ? (
                          plantScoped && !allowsPlant(pa.plantCode) ? (
                            // Not assigned to this user — show the chip but
                            // don't link it (no access to the plant page).
                            <span
                              className="badge plant"
                              title="You don't have access to this plant"
                            >
                              {pa.plantCode}
                            </span>
                          ) : (
                            <Link
                              to={`/plants/${encodeURIComponent(pa.plantCode)}`}
                              style={{ textDecoration: 'none' }}
                            >
                              <span className="badge plant">
                                {pa.plantCode}
                              </span>
                            </Link>
                          )
                        ) : (
                          '—'
                        )}
                        {/* Service name, shown only when it isn't the
                            default — so a plant listed twice (Standard
                            Coverage + Lineside Support) reads clearly
                            and single-service rows look as before. */}
                        {serviceLabel(pa) && (
                          <div
                            className="muted"
                            style={{ fontSize: 12, marginTop: 4 }}
                          >
                            {serviceLabel(pa)}
                          </div>
                        )}
                      </td>
                      {!isLiaisonManager && (
                        <td>
                          {formatHourlyRate(pa.hourlyRate) || '—'}
                          <UpcomingChange
                            history={pa.hourlyRateHistory}
                            describe={(h) => formatHourlyRate(h.hourlyRate)}
                          />
                        </td>
                      )}
                      {!isMobile && (
                        <td>
                          {pa.hoursAllocated === '' ||
                          pa.hoursAllocated == null
                            ? '—'
                            : pa.hoursAllocated}
                          <UpcomingChange
                            history={pa.coverageHistory}
                            describe={(h) => formatCoverage(h)}
                          />
                        </td>
                      )}
                      {!isMobile && <td>{hoursUnitLabel}</td>}
                      <td>
                        {totalPerDay == null ? '—' : formatHours(totalPerDay)}
                        {weekendNote(pa) && (
                          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                            {weekendNote(pa)}
                          </div>
                        )}
                      </td>
                      {!isMobile && isDirector && (
                        <td>{pa.currentPO || '—'}</td>
                      )}
                      <td>
                        <CoverageStatusBadge
                          status={pa.coverageStatus || 'Active'}
                        />
                      </td>
                      {!isMobile && <td>{formatStartDate(pa.startDate) || '—'}</td>}
                      {!isMobile && (
                        <td>
                          {formatStartDate(pa.endDate) || '—'}
                          {assignmentHasEnded(pa) && (
                            <span
                              className="badge"
                              title="Coverage end date has passed"
                              style={{
                                marginLeft: 6,
                                background: '#f3f4f6',
                                color: '#6b7280',
                                fontSize: 11,
                              }}
                            >
                              Ended
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                    {/* Notes sub-row — only renders when the plant
                        assignment has a non-blank note. Spans every
                        column so multi-line notes have room to wrap.
                        Subdued background visually pairs the row with
                        the plant row above. */}
                    {noteText && (
                      <tr>
                        <td
                          colSpan={
                            isMobile
                              ? isLiaisonManager
                                ? 4
                                : 5
                              : isDirector
                                ? 9
                                : isLiaisonManager
                                  ? 7
                                  : 8
                          }
                          style={{
                            background: '#f8fafc',
                            borderTop: 'none',
                            paddingTop: 4,
                            paddingBottom: 10,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                              color: 'var(--brand-muted)',
                              marginBottom: 4,
                            }}
                          >
                            Notes
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              whiteSpace: 'pre-wrap',
                              color: 'var(--brand-ink)',
                            }}
                          >
                            {noteText}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          ) : (
            <p className="muted" style={{ marginTop: 0 }}>
              No plant assignments yet.
            </p>
          )}

          {/* Coverage Activity — audit trail of every add / revise /
              remove event on this card. Rendered as a small sub-heading
              plus a three-column table (When / Who / What). Events are
              written by onSave above, after each successful save, and
              capture changes to all four coverage fields (rate, hours,
              coverage unit, coverage status). */}
          <div style={{ marginTop: 20 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'var(--brand-muted)',
                marginBottom: 8,
              }}
            >
              Coverage Activity
            </div>
            {visibleActivity.length === 0 ? (
              <p className="muted" style={{ marginTop: 0 }}>
                No coverage activity yet.
              </p>
            ) : (
              <table className="mini-table">
                <thead>
                  <tr>
                    <th style={{ width: 220 }}>Changed On</th>
                    <th style={{ width: 180 }}>Changed By</th>
                    <th>Change Made</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleActivity.map((ev) => (
                    <tr key={ev.id}>
                      <td>{formatActivityDate(ev.PostedAt || ev.Created)}</td>
                      <td>{ev.PostedBy || '—'}</td>
                      <td>{ev.Summary || describeActivityFallback(ev)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ---------- Supplier Parts (autopopulated) ----------
          This list is populated automatically from the Parts page — adding
          a part to this supplier over there makes it show up here. No
          inline "+ Add Part" action, since the Parts page is the single
          source of truth for creating parts. */}
      <div className="card">
        <div className="card-title">Supplier Parts</div>
        {parts.length ? (
          <table className="mini-table">
            <thead>
              <tr>
                <th style={{ width: PLANT_COL_WIDTH }}>Plant/s</th>
                <th>Vehicles</th>
                <th>Part name</th>
                <th>Part #</th>
                <th>Variants</th>
              </tr>
            </thead>
            <tbody>
              {parts.map((p) => (
                <tr
                  key={p.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    p.PartCode &&
                    navigate(`/parts/${encodeURIComponent(p.PartCode)}`)
                  }
                >
                  {/* Plant/vehicle pairs are split across two columns but
                      each pair occupies its own "row" inside the cells,
                      so the plant on line N aligns with the vehicle on
                      line N. `width: fit-content` keeps each pill sized
                      to its own text, and a matching min-height per row
                      keeps the two columns vertically synced even when
                      a pair is missing a vehicle. */}
                  <td>
                    {p.plantVehicles?.length ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}
                      >
                        {p.plantVehicles.map((pv, i) => (
                          <span
                            key={i}
                            className="badge plant"
                            style={{ width: 'fit-content' }}
                          >
                            {pv.plantCode}
                          </span>
                        ))}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {p.plantVehicles?.length ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}
                      >
                        {p.plantVehicles.map((pv, i) =>
                          pv.vehicle ? (
                            <span
                              key={i}
                              className="badge vehicle"
                              style={{ width: 'fit-content' }}
                            >
                              {pv.vehicle}
                            </span>
                          ) : (
                            <span
                              key={i}
                              className="muted"
                              style={{ fontSize: 12 }}
                            >
                              —
                            </span>
                          ),
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{p.Title || '—'}</td>
                  <td>{p.PartNumber || '—'}</td>
                  <td>{p.variants?.length || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">
            No parts linked. They'll appear automatically when a part is
            linked to this supplier on the <Link to="/parts">Parts</Link> page.
          </p>
        )}
      </div>

      {/* Notes — Director-only (per Jillian, Sep 2026). */}
      {isDirector && supplier.Notes && (
        <div className="card">
          <div className="card-title">Notes</div>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{supplier.Notes}</p>
        </div>
      )}

      {/* ---- Delete (Director only) ----------------------------------- */}
      {/* Matches the inline "Delete Part" / "Delete Team Member" pattern —
          red-outlined button on the left, muted instructional text on the
          right. Sits at the very bottom of the page so it's hard to click
          by accident while scanning the supplier record. */}
      {canEditHere && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginTop: 16,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            className="btn btn-outline"
            onClick={onDelete}
            style={{
              color: 'var(--brand-danger)',
              borderColor: 'var(--brand-danger)',
              flexShrink: 0,
            }}
          >
            Delete Supplier
          </button>
          <span className="muted" style={{ fontSize: 12, lineHeight: 1.3 }}>
            Only delete supplier if added by mistake. If supplier account
            is closed, change their Coverage Status to Closed.
          </span>
        </div>
      )}

      {/* ---- Documents ----
          Per-supplier file storage (POs, contracts, COIs, W-9s). The
          whole section is Director-only (per Jillian, July 2026) —
          RMs and override-editors like Jim Deslierres don't see it at
          all. Directors (canUpload) can upload and remove. */}
      {isDirector && (
      <div className="card" style={{ marginTop: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          <div className="card-title" style={{ marginBottom: 0 }}>
            Documents
          </div>
          {canUpload && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setDocUploadError('');
                setShowDocUpload(true);
              }}
            >
              + Upload Document
            </button>
          )}
        </div>
        {docsLoading ? (
          <Loading />
        ) : (
          <ResourcesGrid
            // The whole section only renders for Directors now, so no
            // per-category filtering is needed here anymore.
            resources={documents}
            canManage={canUpload}
            onRemove={onRemoveDocument}
            emptyMessage={
              canUpload
                ? 'No documents yet. Click “+ Upload Document” to add contracts, certificates of insurance, W-9s, and other files.'
                : 'No documents on file for this supplier yet.'
            }
          />
        )}
      </div>
      )}

      <Modal
        open={showDocUpload}
        onClose={() => (uploadingDoc ? null : setShowDocUpload(false))}
        title="Upload Supplier Document"
      >
        {showDocUpload && (
          <ResourceForm
            saving={uploadingDoc}
            error={docUploadError}
            onSave={onUploadDocument}
            onCancel={() => setShowDocUpload(false)}
            categoryPlaceholder="e.g. Contract, COI, W-9 (optional)"
            submitLabel="Upload Document"
          />
        )}
      </Modal>

      <Modal
        open={editing}
        onClose={() => (saving ? null : setEditing(false))}
        title={`Edit ${supplier.Title}`}
      >
        {editing && (
          <SupplierForm
            initial={supplier}
            plants={plants}
            // Whoever may edit suppliers (Directors, plus anyone granted a
            // supplier-edit override) can edit the Account Number on an
            // existing supplier — see SupplierForm for the warning UI + the
            // self-aware uniqueness check. existingCodes feeds that
            // duplicate detector.
            isDirector={canEditSuppliers}
            // Billing & PO fields are Director-only, even for editors
            // granted a supplier-edit override (per Jillian, July 2026).
            canSeeBilling={isDirector}
            existingCodes={existingCodes}
            saving={saving}
            onSave={onSave}
            onCancel={() => setEditing(false)}
          />
        )}
      </Modal>

    </>
  );
}

function DetailGrid({ children }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, value, full }) {
  return (
    <div style={full ? { gridColumn: '1 / -1' } : undefined}>
      <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>{label}</div>
      <div style={{ fontSize: 14 }}>{value || <span className="muted">—</span>}</div>
    </div>
  );
}

// Coerce a date-ish value (Date, YYYY-MM-DD, or anything Date can parse)
// to a bare YYYY-MM-DD string for safe lexical comparison and sorting.
// Returns '' on failure so callers can short-circuit. Avoids timezone
// drift by reading the calendar parts directly when given an ISO string.
function toIsoDate(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${dd}`;
}

// Render the stored HourlyRate (number | null | string) as "$45.00 / hr".
// Empty / null / non-numeric falls through to the Field helper's em-dash.
function formatHourlyRate(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `$${n.toFixed(2)} / hr`;
}

// Convert a per-unit hours figure into hours per day so the coverage
// table can surface a single apples-to-apples number.
//   Per Day   → hoursAllocated (already per day)
//   Per Shift → hoursAllocated × shiftsPerDay
//   Per Week  → hoursAllocated ÷ 5   (5-day workweek assumption)
//   Per Month → hoursAllocated ÷ 20  (20-workday-month assumption)
//   Other / blank → null (can't infer without more context)
// Returns a Number or null.
function computeHoursPerDay(hoursAllocated, hoursUnit, shiftsPerDay) {
  const h = Number(hoursAllocated);
  if (!Number.isFinite(h)) return null;
  const unit = (hoursUnit || '').toLowerCase();
  if (unit === 'per day') return h;
  if (unit === 'per shift') {
    const s = Number(shiftsPerDay);
    if (!Number.isFinite(s) || s <= 0) return null;
    return h * s;
  }
  if (unit === 'per week') return h / 5;
  if (unit === 'per month') return h / 20;
  return null;
}

// Pretty-print a computed hours-per-day number: show up to 2 decimals
// but trim trailing zeros so "2.00" renders as "2" and "1.5" stays "1.5".
function formatHours(n) {
  if (!Number.isFinite(n)) return '—';
  const rounded = Math.round(n * 100) / 100;
  return String(parseFloat(rounded.toFixed(2)));
}

// Small colored pill for a plant's coverage lifecycle. Blue =
// Prospecting (early stage, pre-Active), purple = Proposal Out (sits
// between Prospecting and Active), green = Active, teal = On Call
// (active but standing by, not currently working), amber = Paused,
// orange = Suspended (more serious than Paused — compliance /
// dispute hold), red = Closed (relationship ran and ended), gray =
// Declined (terminal "no" — never started). Mirrors the part-status
// palette used on Parts.jsx / PartDetail.jsx so the visual language
// is consistent across the portal.
function CoverageStatusBadge({ status }) {
  const palette = {
    Prospecting: { bg: 'rgba(30, 64, 175, 0.1)', fg: '#1e40af' },
    'Proposal Out': { bg: 'rgba(126, 34, 206, 0.1)', fg: '#7e22ce' },
    Active: { bg: 'rgba(22, 163, 74, 0.1)', fg: 'var(--brand-success, #16a34a)' },
    'On Call': { bg: 'rgba(13, 148, 136, 0.12)', fg: '#0d9488' },
    Paused: { bg: 'rgba(202, 138, 4, 0.1)', fg: '#ca8a04' },
    Suspended: { bg: 'rgba(234, 88, 12, 0.12)', fg: '#ea580c' },
    Closed: { bg: 'rgba(220, 38, 38, 0.1)', fg: 'var(--brand-danger)' },
    Declined: { bg: 'rgba(100, 116, 139, 0.12)', fg: '#475569' },
  };
  const colors = palette[status] || palette.Active;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 999,
        background: colors.bg,
        color: colors.fg,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {status}
    </span>
  );
}

// Format a YYYY-MM-DD (or ISO) date string into MM/DD/YYYY for display.
// Falls back to the raw value when it can't parse cleanly so we never hide
// data outright.
function formatPaperworkDate(value) {
  if (!value) return '';
  const s = String(value);
  // Match YYYY-MM-DD prefix to avoid timezone drift from new Date().
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
  }
  return s;
}

// ---- Plant Locations & Coverage activity ---------------------------
// Diff + formatting helpers live here (rather than in services/) because
// they're specific to how this page phrases the activity feed. A future
// caller would probably want to tweak wording, so keeping the logic at
// the UI layer keeps that flexible.

// Which fields on a plant-assignment row count as "part of the record" for
// the purpose of deciding a Revised vs. unchanged row. If any of these
// differs between old and new, we log a Revised event.
const TRACKED_PA_FIELDS = [
  'hourlyRate',
  'hoursAllocated',
  'hoursUnit',
  'hoursUnitOther',
  'coverageStatus',
  'currentPO',
  'startDate',
  'endDate',
  'billSaturdays',
  'billSundays',
  'notes',
];

// Weekend billing flags: missing (older rows) means NOT billed, so compare
// and display them as plain yes/no.
const WEEKEND_FLAGS = new Set(['billSaturdays', 'billSundays']);
const paFieldValue = (field, value) =>
  WEEKEND_FLAGS.has(field) ? value === true : value;

/**
 * Compare two plant-assignment arrays (before/after a save) and return an
 * ordered list of { type, plantCode, summary } objects describing the
 * changes. One event per plant — a single-shot save that bumps both the
 * hourly rate and the hours allocated on SHAP produces one 'Revised' row,
 * not two.
 *
 * Plants keyed by upper-cased plantCode; untagged rows (blank plantCode)
 * are ignored because the log needs a handle.
 */
function diffPlantAssignments(oldList, newList) {
  const indexByCode = (list) => {
    const out = new Map();
    (list || []).forEach((pa) => {
      const code = String(pa.plantCode || '').trim().toUpperCase();
      if (code) out.set(code, pa);
    });
    return out;
  };
  const before = indexByCode(oldList);
  const after = indexByCode(newList);
  const changes = [];

  // Numeric coercion for hoursAllocated — '' / null / undefined → 0 so an
  // Added row (no prior value) still produces a positive hoursAdded delta
  // for the Sales Tracker.
  const numHours = (v) => {
    if (v === '' || v === null || v === undefined) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // Added + Revised — iterate the new map so added rows get listed in the
  // order they now appear on the form.
  for (const [code, paNew] of after) {
    const paOld = before.get(code);
    if (!paOld) {
      changes.push({
        type: 'Added',
        plantCode: code,
        summary: `Added ${code}: ${describePlantAssignment(paNew)}.`,
        previousHours: 0,
        newHours: numHours(paNew.hoursAllocated),
        // Brand-new pair didn't exist before, so previousStatus is empty.
        // The Sales-Tracker effective-hours math treats anything other
        // than 'Active' as zero, so an Added row with new status =
        // Prospecting / Proposal Out / etc. logs HoursAdded = 0 (which
        // the logger drops) and an Added row with new status = Active
        // logs HoursAdded = newHours.
        previousStatus: '',
        newStatus: paNew.coverageStatus || '',
        hoursUnit: paNew.hoursUnit || '',
      });
      continue;
    }
    // Dated history changes (Oct 2026). When a history changed, its entry
    // list says it all, so the matching top-level fields are skipped to
    // avoid saying the same thing twice.
    const covParts = historyDiffParts(
      paOld.coverageHistory,
      paNew.coverageHistory,
      'Coverage',
      (h) => formatCoverage(h) || `${h.hoursAllocated ?? 0} Hours`,
    );
    const rateParts = historyDiffParts(
      paOld.hourlyRateHistory,
      paNew.hourlyRateHistory,
      'Rate',
      (h) => formatPaFieldValue('hourlyRate', h.hourlyRate),
    );
    const skip = new Set([
      ...(covParts.length ? ['hoursAllocated', 'hoursUnit', 'hoursUnitOther'] : []),
      ...(rateParts.length ? ['hourlyRate'] : []),
    ]);
    const fieldDiffs = TRACKED_PA_FIELDS
      .filter((f) => !skip.has(f))
      .filter((f) => !valuesEqual(paFieldValue(f, paOld[f]), paFieldValue(f, paNew[f])))
      .map((f) => ({
        field: f,
        oldVal: paOld[f],
        newVal: paNew[f],
      }));
    if (fieldDiffs.length > 0 || covParts.length || rateParts.length) {
      const parts = [
        ...fieldDiffs.map((d) => {
          const label = PA_FIELD_LABELS[d.field] || d.field;
          return `${label}: ${formatPaFieldValue(d.field, d.oldVal)} → ${formatPaFieldValue(d.field, d.newVal)}`;
        }),
        ...covParts,
        ...rateParts,
      ];
      // Carry old/new hoursAllocated whether or not it changed in this
      // diff — the salesTracker helper computes the delta and zeros it
      // out automatically when the effective (Active-only) hours don't
      // change. Status carries through too so the logger can handle the
      // "flipped to/from Active" cases for the Sales Tracker.
      changes.push({
        type: 'Revised',
        plantCode: code,
        summary: `${code} — ${parts.join('; ')}.`,
        // Latest scheduled coverage, so a change booked for a future date
        // still counts for the Sales Tracker when it's entered (Oct 2026).
        previousHours: numHours(latestHours(paOld)),
        newHours: numHours(latestHours(paNew)),
        previousStatus: paOld.coverageStatus || '',
        newStatus: paNew.coverageStatus || '',
        hoursUnit: paNew.hoursUnit || paOld.hoursUnit || '',
      });
    }
  }

  // Removed — plants that were in the old list but are gone from the new one.
  for (const [code, paOld] of before) {
    if (!after.has(code)) {
      changes.push({
        type: 'Removed',
        plantCode: code,
        summary: `Removed ${code} (was ${describePlantAssignment(paOld)}).`,
        previousHours: numHours(paOld.hoursAllocated),
        newHours: 0,
        // Pair no longer exists, so newStatus is empty. Removing an
        // Active pair will register HoursLost = previousHours; removing
        // a Prospecting / Closed / etc. pair will register zero (and
        // the logger drops it).
        previousStatus: paOld.coverageStatus || '',
        newStatus: '',
        hoursUnit: paOld.hoursUnit || '',
      });
    }
  }
  return changes;
}

// Small gray "Changes to 6 Hours / Week on 11/02/26" line under a plant
// row's rate or hours when a dated change is scheduled (Oct 2026).
function UpcomingChange({ history, describe }) {
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const next = (Array.isArray(history) ? history : [])
    .filter((h) => String(h.effectiveDate) > today)
    .sort((a, b) => (a.effectiveDate < b.effectiveDate ? -1 : 1))[0];
  if (!next) return null;
  const m = String(next.effectiveDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const when = m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : next.effectiveDate;
  const what = describe(next);
  if (!what) return null;
  return (
    <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
      Changes to {what} on {when}
    </div>
  );
}

// Newest coverage hours on a plant assignment (scheduled ones included).
function latestHours(pa) {
  const h = Array.isArray(pa?.coverageHistory) ? pa.coverageHistory : [];
  if (!h.length) return pa?.hoursAllocated;
  let latest = h[0];
  for (const e of h) if (String(e.effectiveDate) > String(latest.effectiveDate)) latest = e;
  return latest.hoursAllocated;
}

// "Coverage change added: 6 Hours / Week from Nov 2, 2026" lines for the
// entries added to / removed from a dated history between two saves.
function historyDiffParts(oldList, newList, noun, describe) {
  const key = (h) => JSON.stringify(h);
  const before = new Map((oldList || []).map((h) => [key(h), h]));
  const after = new Map((newList || []).map((h) => [key(h), h]));
  const when = (h) =>
    h.effectiveDate === '1970-01-01'
      ? 'from the start'
      : `from ${formatStartDate(h.effectiveDate) || h.effectiveDate}`;
  const parts = [];
  for (const [k, h] of after) {
    if (!before.has(k)) parts.push(`${noun} change added: ${describe(h)} ${when(h)}`);
  }
  for (const [k, h] of before) {
    if (!after.has(k)) parts.push(`${noun} change removed: ${describe(h)} ${when(h)}`);
  }
  return parts;
}

// Human-readable labels for the tracked plant-assignment fields. Used to
// phrase the Summary line ("Hourly Rate $45 → $50").
const PA_FIELD_LABELS = {
  hourlyRate: 'Hourly Rate',
  hoursAllocated: 'Coverage Hours',
  hoursUnit: 'Coverage Unit',
  hoursUnitOther: 'Coverage Unit (other)',
  currentPO: 'Current PO',
  notes: 'Notes',
  coverageStatus: 'Coverage Status',
  startDate: 'Start Date',
  endDate: 'End Date',
  billSaturdays: 'Saturday Billed',
  billSundays: 'Sunday Billed',
};

// "Weekends not billed" style note for the plant table (Nov 2026). Blank
// when both weekend days are billed, or for weekly / monthly coverage,
// where the weekend checkboxes don't apply.
function weekendNote(pa) {
  const unit = String(pa?.hoursUnit || '').trim().toLowerCase();
  if (unit === 'per week' || unit === 'per month') return '';
  const sat = pa?.billSaturdays === true;
  const sun = pa?.billSundays === true;
  if (sat && sun) return '';
  if (!sat && !sun) return 'Weekends not billed';
  return !sat ? 'Saturdays not billed' : 'Sundays not billed';
}

// Format a stored ISO start date (YYYY-MM-DD) for display. Parses
// the date components manually instead of letting Date interpret the
// string as UTC midnight, which would otherwise shift the rendered
// date back a day for users in negative-offset timezones. Returns
// '' for empty / invalid inputs so callers can render '—' uniformly.
function formatStartDate(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(iso);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Format a single plant-assignment field value for inclusion in the
// Summary string. Keeps $ on rates, em-dash on blanks, and falls through
// to String() for the rest. Separate from the table's formatHourlyRate
// because Summary is a one-liner where "$45.00 / hr" feels wordy.
function formatPaFieldValue(field, value) {
  if (WEEKEND_FLAGS.has(field)) return value === true ? 'Yes' : 'No';
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'hourlyRate') {
    const n = Number(value);
    return Number.isFinite(n) ? `$${n.toFixed(2)}/hr` : String(value);
  }
  // Render start dates as "May 13, 2026" in activity summaries so the
  // log reads naturally instead of "2026-05-13".
  if (field === 'startDate' || field === 'endDate') {
    return formatStartDate(value) || '—';
  }
  return String(value);
}

// One-line description of a plant-assignment row, for Added / Removed
// summaries ("Added SHAP: $45/hr, 8 Per Shift, Active").
function describePlantAssignment(pa) {
  const bits = [];
  const rate = formatPaFieldValue('hourlyRate', pa.hourlyRate);
  if (rate !== '—') bits.push(rate);
  const hours = pa.hoursAllocated;
  const unit = pa.hoursUnit;
  if ((hours !== '' && hours != null) || unit) {
    bits.push(`${hours ?? '?'}${unit ? ' ' + unit : ''}`);
  }
  if (pa.coverageStatus) bits.push(pa.coverageStatus);
  return bits.length ? bits.join(', ') : 'no fields set';
}

// Equality check that treats '' / null / undefined as the same value so a
// blank field that stays blank doesn't register as a change. Numbers are
// compared as numbers to avoid "8" vs 8 noise from form inputs.
function valuesEqual(a, b) {
  const na = a === null || a === undefined || a === '' ? '' : a;
  const nb = b === null || b === undefined || b === '' ? '' : b;
  if (na === '' && nb === '') return true;
  const fa = Number(na);
  const fb = Number(nb);
  if (Number.isFinite(fa) && Number.isFinite(fb)) return fa === fb;
  return String(na) === String(nb);
}

// Date formatter for the Activity table — "Apr 23, 2026, 2:14 PM". Falls
// back to the raw string if the timestamp can't be parsed, so a bad row
// never renders as a blank cell.
function formatActivityDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Fallback "What" description when a historical row is missing the Summary
// column (shouldn't happen going forward — the column is now provisioned —
// but keeps older rows readable rather than showing blank). Uses the
// EventType + PlantCode fields that always exist.
function describeActivityFallback(ev) {
  const type = ev.EventType || 'Updated';
  const plant = ev.PlantCode || '—';
  return `${type} ${plant}`;
}

/**
 * Return the list of contact rows that appear in the new list but not
 * the old one. Matching is by normalised name (case-insensitive,
 * trimmed) — if someone fixes a typo on an existing row we don't want to
 * log it as an Add. Blank-named rows are skipped because they're usually
 * placeholder rows the user added but didn't fill in yet.
 */
function diffAddedContacts(oldList, newList) {
  const norm = (s) => String(s || '').trim().toLowerCase();
  const oldNames = new Set(
    (oldList || []).map((c) => norm(c.name)).filter(Boolean),
  );
  return (newList || []).filter((c) => {
    const n = norm(c.name);
    if (!n) return false;
    return !oldNames.has(n);
  });
}
